/**
 * @kia/shared — pricing-accuracy contracts.
 *
 * Price-drift monitor (پایش انحراف قیمت): material is ~76% of a motor's cost, so a stale material price
 * is the single biggest source of pricing error. This surfaces, spend-weighted, the top materials whose
 * BOOK price (the one that costs products) lags the most recent ACTUAL PAID price — either the material's
 * own purchase or, for a group-priced material, a recent purchase of a group peer. The paid prices already
 * live in `po_receipts`; this only READS them. Read-only; adopting a price reuses the existing setMat route.
 */
/**
 * The magnitude of a price change as a fraction of the CURRENT book price — `|next − book| / book` — or null
 * when there is no positive current book to compare against (a first-time set). The write-time confirm guard
 * (routes/prices.ts) refuses a change beyond `priceJumpConfirmPct` unless explicitly confirmed, catching the
 * fat-finger / OCR 10× (toman↔rial) error that would otherwise write straight to the book.
 */
export function priceJumpPct(book, next) {
    if (book == null || book <= 0)
        return null;
    return Math.abs(next - book) / book;
}
/**
 * THE suggested-price formula (CW-07): the price that hits `margin` exactly — `cogs ÷ (1 − margin)`, rounded
 * to the rial. Returns 0 when the cost is unknown (≤ 0) or the margin is unreachable (≥ 1). Every surface
 * that suggests a price — the in-app hint, the AI daily brief, the pre-sale sense-check, product search —
 * calls THIS, so a motor never shows two different "suggested" prices. The app steers to the owner's TARGET
 * margin (his aim), per the margin-thread decision; the floor stays the guardrail the realised margin clears.
 */
export function suggestedPrice(cogs, margin) {
    return cogs > 0 && margin < 1 ? Math.round(cogs / (1 - margin)) : 0;
}
/**
 * THE gross-margin formula (CW-07): `(revenue − cost) / revenue`, or null when there is no positive revenue
 * to take a margin on. One definition so "margin" means the same number on every surface — deal economics,
 * realisation, the pre-sale sense-check, the list-price sweep, and the UI hint — and a change to how margin
 * treats a zero/negative denominator can't silently diverge between them.
 */
export function grossMargin(revenue, cost) {
    return revenue > 0 ? (revenue - cost) / revenue : null;
}
/**
 * PREPROD-1 — the human name of what is DRIVING the suggested factor, as ONE authority both planes read.
 *
 * This lived in `services/market.ts` (BIDX-08) where only the server could reach it, so the web card
 * hand-rolled a BINARY basket/دلار collapse of this FIVE-valued field and asserted «سبد فلزات پیشران» in
 * its titles unconditionally — on a starved-basket book (factorSource `fx`/`material`/`none`) that states a
 * false driver. Adding a third copy in the web was the OPS-01 trap, so the definition moved here instead:
 * `@kia/shared` is the only module both packages import (the web cannot import server code).
 *
 * The strings are UNCHANGED from the server original — the owner reads the bell and the card as one
 * vocabulary, and a second name for one object is a second object as far as the reader is concerned.
 */
export function indexDriverLabel(src) {
    return src === 'basket'
        ? 'سبد فلزات پیشران'
        : src === 'fx'
            ? 'حرکت دلار'
            : src === 'material'
                ? 'میانگین نرخ مواد'
                : src === 'override'
                    ? 'ضریب دستی'
                    : 'بازار';
}
//# sourceMappingURL=pricing.js.map